/**
 * Payment Intents API
 * Client for booking request payment intent endpoints
 * Follows the same pattern as the generated client in packages/libs/api
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  UseMutationOptions,
} from '@tanstack/react-query';

import { legacyApiInstance, type ErrorType } from './legacyApiInstance';

// Type definitions
export interface CreatePaymentIntentRequest {
  amount_cents: number;
  currency?: string;
}

export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  managed_account_token?: string;
  is_connected_account?: boolean;
}

export interface PaymentIntentDetails {
  id: string;
  status: string;
  amount: number;
  currency: string;
  client_secret: string;
}

// Error type for payment intent operations
export interface PaymentIntentError {
  error: string;
}

export interface AttachCustomerRequest {
  email?: string;
  name?: string;
}

export interface AttachCustomerResponse {
  customer_id: string;
}

/**
 * Create payment intent
 */
export const createPaymentIntent = (
  bookingRequestId: string,
  createPaymentIntentRequest: CreatePaymentIntentRequest
) => {
  return legacyApiInstance<PaymentIntentResponse>({
    url: `/api/booking_requests/${bookingRequestId}/payment_intents`,
    method: 'POST',
    data: {
      amount_cents: createPaymentIntentRequest.amount_cents,
      currency: createPaymentIntentRequest.currency || 'usd',
    },
  });
};

export const getCreatePaymentIntentMutationOptions = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    TError,
    { bookingRequestId: string; data: CreatePaymentIntentRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createPaymentIntent>>,
  TError,
  { bookingRequestId: string; data: CreatePaymentIntentRequest },
  TContext
> => {
  const mutationKey = ['createPaymentIntent'];
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    { bookingRequestId: string; data: CreatePaymentIntentRequest }
  > = (props) => {
    const { bookingRequestId, data } = props ?? {};
    return createPaymentIntent(bookingRequestId, data);
  };

  return { mutationKey, mutationFn, ...mutationOptions };
};

export type CreatePaymentIntentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createPaymentIntent>>
>;
export type CreatePaymentIntentMutationBody = CreatePaymentIntentRequest;
export type CreatePaymentIntentMutationError = ErrorType<PaymentIntentError>;

/**
 * Hook to create payment intent
 */
export const useCreatePaymentIntent = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createPaymentIntent>>,
    TError,
    { bookingRequestId: string; data: CreatePaymentIntentRequest },
    TContext
  >;
}) => {
  const mutationOptions = getCreatePaymentIntentMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Update payment intent
 */
export const updatePaymentIntent = (
  bookingRequestId: string,
  paymentIntentId: string,
  updatePaymentIntentRequest: CreatePaymentIntentRequest
) => {
  return legacyApiInstance<PaymentIntentDetails>({
    url: `/api/booking_requests/${bookingRequestId}/payment_intents/${paymentIntentId}`,
    method: 'PATCH',
    data: {
      amount_cents: updatePaymentIntentRequest.amount_cents,
      currency: updatePaymentIntentRequest.currency || 'usd',
    },
  });
};

export const getUpdatePaymentIntentMutationOptions = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePaymentIntent>>,
    TError,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: CreatePaymentIntentRequest;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updatePaymentIntent>>,
  TError,
  {
    bookingRequestId: string;
    paymentIntentId: string;
    data: CreatePaymentIntentRequest;
  },
  TContext
> => {
  const mutationKey = ['updatePaymentIntent'];
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updatePaymentIntent>>,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: CreatePaymentIntentRequest;
    }
  > = (props) => {
    const { bookingRequestId, paymentIntentId, data } = props ?? {};
    return updatePaymentIntent(bookingRequestId, paymentIntentId, data);
  };

  return { mutationKey, mutationFn, ...mutationOptions };
};

/**
 * Hook to update payment intent
 */
export const useUpdatePaymentIntent = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updatePaymentIntent>>,
    TError,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: CreatePaymentIntentRequest;
    },
    TContext
  >;
}) => {
  const mutationOptions = getUpdatePaymentIntentMutationOptions(options);

  return useMutation(mutationOptions);
};

/**
 * Attach customer to payment intent
 */
export const attachCustomerToPaymentIntent = (
  bookingRequestId: string,
  paymentIntentId: string,
  attachCustomerRequest: AttachCustomerRequest
) => {
  return legacyApiInstance<AttachCustomerResponse>({
    url: `/api/booking_requests/${bookingRequestId}/payment_intents/${paymentIntentId}/attach_customer`,
    method: 'POST',
    data: {
      email: attachCustomerRequest.email,
      name: attachCustomerRequest.name,
    },
  });
};

export const getAttachCustomerMutationOptions = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof attachCustomerToPaymentIntent>>,
    TError,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: AttachCustomerRequest;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof attachCustomerToPaymentIntent>>,
  TError,
  {
    bookingRequestId: string;
    paymentIntentId: string;
    data: AttachCustomerRequest;
  },
  TContext
> => {
  const mutationKey = ['attachCustomerToPaymentIntent'];
  const { mutation: mutationOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof attachCustomerToPaymentIntent>>,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: AttachCustomerRequest;
    }
  > = (props) => {
    const { bookingRequestId, paymentIntentId, data } = props ?? {};
    return attachCustomerToPaymentIntent(
      bookingRequestId,
      paymentIntentId,
      data
    );
  };

  return { mutationKey, mutationFn, ...mutationOptions };
};

/**
 * Hook to attach customer to payment intent
 */
export const useAttachCustomerToPaymentIntent = <
  TError = ErrorType<PaymentIntentError>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof attachCustomerToPaymentIntent>>,
    TError,
    {
      bookingRequestId: string;
      paymentIntentId: string;
      data: AttachCustomerRequest;
    },
    TContext
  >;
}) => {
  const mutationOptions = getAttachCustomerMutationOptions(options);

  return useMutation(mutationOptions);
};
